import React, { useEffect, useState } from "react";
import { DataTable } from "../../ui/DataTable";
import { _ROUTES } from "../../../utils/_routes_constants";
import CustomGroupHeader from "../sales-filaire/CustomGroupHeader";
import { ColumnBuilder } from "./ColumnBuilder";
import { DataService } from "../../../services/DataService";
import { useSalesOnCellValueChanged } from "./useSaveSalesOnCellChanged";
import { AvgPriceValue } from "./AvgPriceValue";
import { defaultExcelStyle } from "../../../utils/AgGridUtils/defaultExcelStyle";
import { _ACTIVITIES } from "../../../utils/constants";
import { parseInternationalNumber } from "../BaseBudgetSaveFunctions";

export const SUB_THEMES = {
  EXTERNAL_CASH: "External Cash",
  EXTERNAL_CREDIT: "External Credit",
  INTERNAL: "Internal",
  VA: "Valentina",
  TR: "Terre Rouge",
  EXT_VOL: "External Volumes",
  INT_VOL: "Internal Volumes",
  EXT_VALUES: "External Values",
  INT_VALUES: "Internal Values",
};

export const TOTAL_NAME = {
  QUOTE_PART: "Quote-part",
  TOTAL_QTY: "Total Qty",
  TOTAL_TURN_OVER: "TOTAL TURN OVER",
};

export const THEME_SALE_BLK = {
  INPUT_BLK_MONTH: "InputBlockMonth",
  DEPOT: "Depot",
  VALUES: "Values",
  TURNOVER: "TURNOVER Rs.",
};

/**
 * @param params
 * @constructor
 */
export const AgGridSaleBlock = ({ params, gridRef }) => {
  const [rowsData, setRowsData] = useState([]);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const [inputBlockData, setInputBlockData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [rateIncreaseData, setRateIncreaseData] = useState([]);
  const dataService = new DataService();

  useEffect(() => {
    dataService.fetchData(_ROUTES.IPT_SALE_BLOCK_GROUP, params, (data) => {
      const transformedRows = data
        .map((themeData) =>
          themeData.subThemes.map((subTheme) => {
            const row = {
              theme: themeData.theme,
              subThemes: subTheme.name,
              total: 0,
              inputs: {},
            };

            for (let i = 1; i <= 12; i++) {
              row[i] = null;
            }

            subTheme.inputs.forEach((input) => {
              row[input.fiscalMonth] = input.value;
              row.inputs[input.fiscalMonth] = input.id;
            });

            return row;
          }),
        )
        .flat();

      setRowsData(transformedRows);
    });
  }, [pendingRefresh, params.site, params]);

  useEffect(() => {
    dataService.fetchData(
      _ROUTES.INPUT_RATE_INCREASE,
      {
        ...params,
        "activity.code": _ACTIVITIES.BLOCK,
      },
      (data) => {
        const formattedData = data.map((row) => ({
          [row.fiscalMonth]: row.pcIncrease,
        }));
        setRateIncreaseData(formattedData);
      },
    );
  }, [params.site, params]);

  useEffect(() => {
    setColumns(ColumnBuilder(inputBlockData, rateIncreaseData));
  }, [inputBlockData, rateIncreaseData]);

  const handleCellChange = useSalesOnCellValueChanged(
    params,
    setPendingRefresh,
  );

  const defaultColDef = {
    valueParser: (params) => {
      // Parser pour tous les champs éditables numériques
      if (params.colDef.editable && typeof params.newValue === "string") {
        return parseInternationalNumber(params.newValue);
      }
      return params.newValue;
    },
    cellClassRules: {
      "blank-row": (params) => {
        return params.data?._SPACER;
      },
      "total-header-col": (params) => {
        return params.colDef.field === "total";
      },
      "total-row": (params) =>
        params.node && params.node.aggData && params.node.footer,
    },
  };

  const excelStyles = defaultExcelStyle();

  return (
    <>
      <AvgPriceValue
        params={params}
        inputBlockData={inputBlockData}
        setInputBlockData={setInputBlockData}
      />
      <DataTable
        gridRef={gridRef}
        rows={rowsData}
        columns={columns}
        onCellValueChanged={handleCellChange}
        excelStyles={excelStyles}
        fullHeight={true}
        fileNameCsvExport={"sale-block"}
        agGridProps={{
          getRowId: (params) => {
            return params.data.subThemes;
          },
          cellSelection: true,
          copyHeadersToClipboard: false,
          defaultColDef: defaultColDef,
          groupDisplayType: "groupRows",
          groupDefaultExpanded: -1,
          suppressAggFuncInHeader: true,
          suppressClipboardPaste: false,
          enableCellTextSelection: true,
          ensureDomOrder: true,
          groupRowRendererParams: {
            suppressCount: true,
          },
          groupRowRenderer: (params) => {
            if (params.value !== THEME_SALE_BLK.DEPOT) return "";
            return <CustomGroupHeader node={params} />;
          },
          groupTotalRow: "bottom",
        }}
      />
    </>
  );
};

export default AgGridSaleBlock;
